import { useEffect, useRef, useState } from "react";
import type { HolidayFakeHuntConfig, HolidayFakeHuntProfile } from "../scenario/types";

type Zone = HolidayFakeHuntProfile["tell"]["zone"];

const ZONES: { id: Zone; hint: string }[] = [
  { id: "photo", hint: "Photo" },
  { id: "name", hint: "Name" },
  { id: "joined", hint: "Joined" },
  { id: "company", hint: "Company" },
  { id: "activity", hint: "Activity" },
  { id: "contact", hint: "Contact" },
];

const TIPS = [
  { title: "Age vs seniority", body: "A Partner who joined last week with 500+ connections and no history is almost never real." },
  { title: "Lookalike names", body: "Clones copy a photo and title, then change one letter. Sitting executives rarely show Open to work." },
  { title: "Off-platform contact", body: "Company recruiters do not hire from a personal Gmail or WhatsApp." },
  { title: "Activity bursts", body: "Years of silence, then a week of conference posts, is a sockpuppet warming up." },
  { title: "One-letter domains", body: "Check the company URL, not the logo. One swapped letter is a classic tell." },
];

const ZONE_HINT: Record<Zone, string> = {
  photo: "Look at the portrait against the name and role. Cloned photos are common.",
  name: "Read the name and headline slowly. One letter, or an Open to work badge, can be the tell.",
  joined: "Compare the join date with the seniority and network size.",
  company: "Check the company name and any website spelling.",
  activity: "Look at posting cadence, not the content of a single post.",
  contact: "See how they want you to leave LinkedIn.",
};

function companyParts(company: string) {
  const [name, site] = company.split(" · ");
  return { name: name.trim(), site: site?.trim() };
}

function contactRows(raw?: string) {
  if (!raw) return [{ label: "Contact info", value: "Visible to 1st-degree connections", locked: true }];
  return raw.split(" · ").map((part) => {
    const value = part.trim();
    if (value.includes("@")) return { label: "Email", value, locked: false };
    if (/whatsapp/i.test(value)) return { label: "WhatsApp", value, locked: false };
    return { label: "Contact", value, locked: false };
  });
}

function headlineMain(headline: string) {
  return headline.replace(/\s*·\s*Open to work/i, "");
}

function profileHint(profile: HolidayFakeHuntProfile) {
  return profile.hint?.trim() || ZONE_HINT[profile.tell.zone];
}

export function HolidayFakeHunt({
  config,
  onDone,
  onStageChange,
}: {
  config: HolidayFakeHuntConfig;
  onDone: () => void;
  onStageChange?: (stage: string) => void;
}) {
  const [index, setIndex] = useState(0);
  const [found, setFound] = useState(false);
  const [miss, setMiss] = useState<string | null>(null);
  const [hintOn, setHintOn] = useState(false);
  const [solved, setSolved] = useState<Set<string>>(() => new Set());
  const [tapped, setTapped] = useState<Set<string>>(() => new Set());
  const [tour, setTour] = useState<number | "done">(0);
  const tourTimer = useRef<number | null>(null);
  const profile = config.profiles[index];
  const requiredCount = Math.min(2, config.profiles.length);
  const canContinue = solved.size >= requiredCount;
  const remaining = config.profiles.length - solved.size;
  const allSolved = remaining <= 0;
  const firm = profile ? companyParts(profile.company) : { name: "", site: undefined };
  const contacts = profile ? contactRows(profile.contact) : [];
  const tell = profile?.tell.zone;

  useEffect(() => {
    if (allSolved) onStageChange?.("done");
    else onStageChange?.(found ? `found-${index}` : `hunt-${index}`);
  }, [allSolved, found, index, onStageChange]);

  useEffect(() => {
    if (canContinue || found || index !== 0 || tour === "done") return;
    tourTimer.current = window.setTimeout(() => {
      setTour((cur) => {
        if (cur === "done") return cur;
        if (typeof cur === "number" && cur >= ZONES.length - 1) return "done";
        return typeof cur === "number" ? cur + 1 : cur;
      });
    }, 1450);
    return () => {
      if (tourTimer.current !== null) window.clearTimeout(tourTimer.current);
    };
  }, [canContinue, found, index, tour]);

  useEffect(() => {
    if (typeof tour !== "number" || tour < 0) return;
    const el = document.querySelector(`[data-hf-zone="${ZONES[tour].id}"]`);
    el?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [tour]);

  const endTour = () => setTour("done");
  const tourZone = typeof tour === "number" && tour >= 0 ? ZONES[tour].id : null;

  const selectProfile = (i: number) => {
    endTour();
    setIndex(i);
    setFound(solved.has(config.profiles[i].id));
    setMiss(null);
    setHintOn(false);
  };

  const pick = (zone: Zone) => {
    endTour();
    if (!profile || found) return;
    setTapped((old) => new Set(old).add(`${profile.id}:${zone}`));
    if (zone === profile.tell.zone) {
      setFound(true);
      setMiss(null);
      setSolved((old) => new Set(old).add(profile.id));
      return;
    }
    setMiss("That can be real. Attackers copy photos, jobs and mutuals. Tap the detail that does not add up.");
  };

  const next = () => {
    const upcoming = config.profiles.findIndex((p, i) => i > index && !solved.has(p.id));
    const wrap = config.profiles.findIndex((p) => !solved.has(p.id));
    const nextIndex = upcoming >= 0 ? upcoming : wrap;
    if (nextIndex < 0) {
      setFound(true);
      return;
    }
    setIndex(nextIndex);
    setFound(false);
    setMiss(null);
    setHintOn(false);
  };

  const revealed = (zone: Zone) => (found && tell === zone ? " hf-revealed" : "");
  const hotClass = (zone: Zone, extra = "") =>
    `hf-hot${revealed(zone)}${tapped.has(`${profile?.id}:${zone}`) ? " hf-tapped" : ""}${tourZone === zone ? " hf-tour-on" : ""}${extra}`;
  const tourChip = (zone: Zone) =>
    tourZone === zone ? <em className="hf-tour-chip">Tap {ZONES.find((z) => z.id === zone)?.hint}</em> : null;

  return (
    <section className="holiday-fakehunt">
      <header className="hf-top">
        <small>{config.kicker ?? "GAME · FAKE PROFILE LAB"}</small>
        <div className="hf-top-row">
          <h1>{config.title}</h1>
          <strong>
            {solved.size} / {requiredCount} required
            {solved.size >= requiredCount
              ? remaining > 0
                ? ` · ${remaining} optional`
                : " · all spotted"
              : ` · ${config.profiles.length} profiles`}
          </strong>
        </div>
        <p>{config.intro} This is an interactive game: open a profile and tap the field that could expose the scam. Fields you already tested remain marked.</p>
      </header>
      {canContinue && (
        <div className="hf-continue-bar">
          <button type="button" className="ht-primary cf-pulse-target" onClick={onDone}>
            Continue<em className="ht-click-chip">Click here</em>
          </button>
          {remaining > 0 ? (
            <span>You can continue now, or investigate the remaining {remaining} profile{remaining === 1 ? "" : "s"}.</span>
          ) : (
            <span>All {config.profiles.length} profiles spotted. Continue when you are ready.</span>
          )}
        </div>
      )}
      <div className="hf-layout">
            <aside className="hf-roster">
              {config.profiles.map((p, i) => (
                <button
                  key={p.id}
                  className={`${i === index ? "on" : ""} ${solved.has(p.id) ? "solved" : ""}`}
                  onClick={() => selectProfile(i)}
                >
                  <span>{p.initials}</span>
                  <b>{p.name}</b>
                  <small>{solved.has(p.id) ? "Spotted" : p.headline}</small>
                </button>
              ))}
            </aside>
            {profile && (
              <div className={`hf-page${tourZone ? " is-touring" : ""}`}>
                {tourZone && (
                  <div className="hf-tour-banner" role="status">
                    Clickable areas light up one by one. Then pick the detail that does not add up.
                  </div>
                )}
                <div className="hf-li-bar" aria-hidden>
                  <i className="hf-li-mark">in</i>
                  <span className="hf-search">Search</span>
                  <nav>Home · My Network · Messaging</nav>
                </div>
                <article className="hf-hero">
                  <div className="hf-cover" aria-hidden />
                  <div className="hf-hero-body">
                    <span data-hf-zone="photo" className={`hf-photo-wrap${tourZone === "photo" ? " hf-tour-on" : ""}`}>
                    <button type="button" className={`hf-photo ${hotClass("photo")}`} onClick={() => pick("photo")} aria-label="Profile photo">
                      {profile.photoUrl ? <img src={profile.photoUrl} alt="" /> : <em>{profile.initials}</em>}
                    </button>
                    {tourChip("photo")}
                    </span>
                    <div data-hf-zone="name" className={`hf-id ${hotClass("name")}`} onClick={() => pick("name")}>
                      <button type="button" className="hf-hot" onClick={() => pick("name")}>
                        <h2>{profile.name}</h2>
                        <p>{headlineMain(profile.headline)}</p>
                      </button>
                      {/open to work/i.test(profile.headline) && <span className="hf-open">Open to work</span>}
                      <p className="hf-meta">{profile.location} · {profile.connections} connections</p>
                      <div className="hf-actions" aria-hidden>
                        <span className="hf-connect">Connect</span>
                        <span className="hf-message">Message</span>
                      </div>
                      {tourChip("name")}
                    </div>
                  </div>
                </article>
                <div className="hf-body">
                <button type="button" data-hf-zone="joined" className={`hf-section ${hotClass("joined")}`} onClick={() => pick("joined")}>
                  <h3>About this profile</h3>
                  <p className="hf-joined-line">{profile.joined}</p>
                  {tourChip("joined")}
                </button>
                <section className="hf-section">
                  <h3>About</h3>
                  <p>{profile.about}</p>
                </section>
                <button type="button" data-hf-zone="company" className={`hf-section ${hotClass("company")}`} onClick={() => pick("company")}>
                  <h3>Experience</h3>
                  <div className="hf-job">
                    <span className="hf-job-mark">{firm.name.slice(0, 1)}</span>
                    <div>
                      <b>{firm.name}</b>
                      <span>{headlineMain(profile.headline)}</span>
                      {firm.site && <code>{firm.site}</code>}
                    </div>
                  </div>
                  {tourChip("company")}
                </button>
                <button type="button" data-hf-zone="activity" className={`hf-section ${hotClass("activity")}`} onClick={() => pick("activity")}>
                  <h3>Activity</h3>
                  <p>{profile.activity}</p>
                  {tourChip("activity")}
                </button>
                <button type="button" data-hf-zone="contact" className={`hf-section ${hotClass("contact")}`} onClick={() => pick("contact")}>
                  <h3>Contact info</h3>
                  {contacts.map((row) => (
                    <div className="hf-contact-row" key={`${row.label}-${row.value}`}>
                      <small>{row.label}</small>
                      <span className={row.locked ? "hf-locked" : undefined}>{row.value}</span>
                    </div>
                  ))}
                  {tourChip("contact")}
                </button>
                </div>
              </div>
            )}
            {profile && (
              <aside className={`hf-rail${found ? " is-hit" : miss ? " is-miss" : ""}`}>
                {found ? (
                  <>
                    <small>PLANTED TELL · {ZONES.find((z) => z.id === tell)?.hint}</small>
                    <h2>{profile.tell.label}</h2>
                    <p>{profile.tell.why}</p>
                    <button
                      type="button"
                      className="ht-primary cf-pulse-target hf-rail-cta"
                      onClick={allSolved ? onDone : next}
                    >
                      {allSolved ? "All four spotted — continue" : "Next profile"}
                      <em className="ht-click-chip">Click here</em>
                    </button>
                  </>
                ) : (
                  <>
                    <small>{miss ? "KEEP LOOKING" : "WHAT TO LOOK FOR"}</small>
                    <h2>{miss ? "That detail can be real" : "Tells that give fakes away"}</h2>
                    {miss ? <p className="hf-rail-miss">{miss}</p> : (
                      <ul className="hf-tips">
                        {TIPS.map((tip) => (
                          <li key={tip.title}>
                            <b>{tip.title}</b>
                            <span>{tip.body}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                    {hintOn ? (
                      <div className="hf-hint">
                        <small>HINT</small>
                        <p>{profileHint(profile)}</p>
                      </div>
                    ) : (
                      <button type="button" className="hf-hint-btn" onClick={() => setHintOn(true)}>
                        Need a hint?
                      </button>
                    )}
                    <p className="hf-prompt">
                      {tourZone
                        ? `Watch for the blue frame — next up: ${ZONES.find((z) => z.id === tourZone)?.hint}.`
                        : "Tap a highlighted field on the profile. One of them is planted."}
                    </p>
                  </>
                )}
              </aside>
            )}
          </div>
    </section>
  );
}
